'use client';

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  IndianRupeeIcon,
  ShoppingBag,
  Users,
  Download,
  Search,
  X,
  Edit,
  Trash2,
} from 'lucide-react';
import { debounce } from 'lodash';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  isWithinInterval,
  startOfDay,
  endOfDay,
  parseISO,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

// 🔹 shadcn/ui imports
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// 🔹 API hooks
import {
  useGetOrdersQuery,
  useGetTransactionStatsQuery,
  useDeleteOrderMutation,
} from '@/features/api/orderApi';

/* =========================================================================
   Reusable Components
   ========================================================================= */

// StatCard
function StatCard({ title, value, icon: Icon, description, iconColor = 'text-primary' }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        {Icon && <Icon className={`h-5 w-5 ${iconColor}`} />}
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-foreground">{value}</div>
        {description && <p className="text-xs text-muted-foreground pt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}

// DateRangeFilter
function DateRangeFilter({ startDate: propStartDate, endDate: propEndDate, onDateRangeChange }) {
  const [dateRange, setDateRange] = useState([propStartDate ?? null, propEndDate ?? null]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    setDateRange([propStartDate ?? null, propEndDate ?? null]);
  }, [propStartDate, propEndDate]);

  const handleSelect = useCallback((update) => {
    setDateRange(update);
    const [start, end] = update;
    onDateRangeChange?.({ startDate: start ?? undefined, endDate: end ?? undefined });
  }, [onDateRangeChange]);

  const resetDates = useCallback(() => {
    setDateRange([null, null]);
    onDateRangeChange?.({ startDate: undefined, endDate: undefined });
  }, [onDateRangeChange]);

  const setPresetRange = useCallback((unit, amount) => {
    const today = new Date();
    let from = new Date();
    if (unit === 'days') from = subDays(today, amount - 1);
    else if (unit === 'months') from = subMonths(today, amount);
    else if (unit === 'years') from = subYears(today, amount);

    const newStart = startOfDay(from);
    const newEnd = endOfDay(today);
    setDateRange([newStart, newEnd]);
    onDateRangeChange?.({ startDate: newStart, endDate: newEnd });
  }, [onDateRangeChange]);

  return (
    <div className="flex flex-col gap-4 mb-6 p-4 border rounded-lg shadow-sm bg-card">
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <DatePicker
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={handleSelect}
          isClearable
          placeholderText="Select date range"
          className="p-2 border rounded-md w-full"
        />
        <button
          onClick={resetDates}
          className="inline-flex items-center gap-2 rounded-md border h-9 px-4 py-2 w-full sm:w-auto hover:bg-accent"
        >
          <X className="w-4 h-4" /> Reset Dates
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { label: 'Last 7 Days', unit: 'days', amount: 7 },
          { label: 'Last 30 Days', unit: 'days', amount: 30 },
          { label: 'Last 6 Months', unit: 'months', amount: 6 },
          { label: 'Last 1 Year', unit: 'years', amount: 1 },
        ].map(({ label, unit, amount }) => (
          <button
            key={label}
            onClick={() => setPresetRange(unit, amount)}
            className="px-3 h-8 rounded-md border text-xs hover:bg-accent"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SalesReportFilters({ searchTerm, onSearchTermChange, onResetSearch }) {
  const debouncedChange = useRef(
    debounce((value) => onSearchTermChange(value), 300)
  ).current;

  const handleInputChange = useCallback(
    (e) => {
      debouncedChange(e.target.value);
    },
    [debouncedChange]
  );

  // cleanup debounce on unmount
  useEffect(() => {
    return () => debouncedChange.cancel();
  }, [debouncedChange]);

  return (
    <div className="flex flex-col sm:flex-row gap-4 items-center w-full">
      <div className="relative w-full sm:max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by ID, customer, product, or date..."
          value={searchTerm}                // ✅ CONTROLLED
          onChange={handleInputChange}
          className="pl-10"
        />
      </div>
      <Button
        onClick={onResetSearch}
        variant="ghost"
        className="flex gap-2"
      >
        <X className="h-4 w-4" /> Reset
      </Button>
    </div>
  );
}

// DailySalesChart
function DailySalesChart({ data, dateRange }) {
  const start = dateRange?.startDate ? new Date(dateRange.startDate) : null;
  const end = dateRange?.endDate ? new Date(dateRange.endDate) : null;

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Daily Sales</CardTitle>
          <CardDescription>No sales data available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily Sales</CardTitle>
        <CardDescription>
          Overview from {start?.toLocaleDateString() || 'start'} to {end?.toLocaleDateString() || 'end'}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
            <YAxis tickFormatter={(v) => `₹${v}`} />
            <Tooltip formatter={(v) => [`₹${v.toFixed(2)}`, 'Sales']} />
            <Bar dataKey="sales" fill="hsl(120, 60%, 45%)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

/* =========================================================================
   Main Dashboard Page
   ========================================================================= */

export default function DashboardPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });
  const [filterStartDate, setFilterStartDate] = useState();
  const [filterEndDate, setFilterEndDate] = useState();

  const navigate = useNavigate();


  const { data: ordersData, isLoading, error } = useGetOrdersQuery({
    search: searchTerm || undefined,
    startDate: filterStartDate?.toISOString(),
    endDate: filterEndDate?.toISOString(),
    page: 1,
    limit: 100,
  });

  
const normalizedOrders = useMemo(() => {
  return (ordersData?.data || []).map((o) => ({
    ...o,
    id: o.transactionId,                
    date: o.date || o.createdAt || null, 
    customerName: o.customerName || o.name || "N/A",
    productName: o.productName || o.courseName || "N/A",
    amount: Number(o.amount || 0),
    email: o.email || "",
    phone: o.phone || "",
  }));
}, [ordersData]);

const allOrders = normalizedOrders;

  const { data: statsData } = useGetTransactionStatsQuery({
    startDate: filterStartDate?.toISOString(),
    endDate: filterEndDate?.toISOString(),
  });

  const [deleteOrder] = useDeleteOrderMutation();

  // Filter + search + sort
const dateFilteredItems = useMemo(() => {
  if (!allOrders.length) return [];

  const start = filterStartDate ? startOfDay(filterStartDate) : undefined;
  const end = filterEndDate ? endOfDay(filterEndDate) : undefined;

  return allOrders.filter((item) => {
    const rawDate = item.date || item.createdAt;
    if (!rawDate) return false;

    const itemDate = parseISO(rawDate);

    if (start && end) return isWithinInterval(itemDate, { start, end });
    if (start) return itemDate >= start;
    if (end) return itemDate <= end;
    return true;
  });
}, [allOrders, filterStartDate, filterEndDate]);


  const searchedItems = useMemo(() => {
  if (!searchTerm) return dateFilteredItems;

  const q = searchTerm.toLowerCase();

  return dateFilteredItems.filter((i) => {
    const searchableFields = [
      i.transactionId,                    // order ID
      i.name,                             // customer name
      i.courseName,                      // product name
      i.email,
      i.phone,
      i.createdAt
        ? new Date(i.createdAt).toLocaleDateString()
        : "",                             // date search
    ];

    return searchableFields.some((field) =>
      String(field || "").toLowerCase().includes(q)
    );
  });
}, [searchTerm, dateFilteredItems]);


const sortedItems = useMemo(() => {
  const items = [...searchedItems];

  return items.sort((a, b) => {
    const dir = sortConfig.direction === "ascending" ? 1 : -1;

    if (sortConfig.key === "date") {
      const aDate = a.date ? new Date(a.date).getTime() : 0;
      const bDate = b.date ? new Date(b.date).getTime() : 0;
      return (aDate - bDate) * dir;
    }

    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];

    if (typeof aVal === "number" && typeof bVal === "number") {
      return (aVal - bVal) * dir;
    }

    return String(aVal || "").localeCompare(String(bVal || "")) * dir;
  });
}, [searchedItems, sortConfig]);


  const handleRequestSort = useCallback((key) => {
    setSortConfig((prev) => (prev?.key === key && prev.direction === 'ascending' ? { key, direction: 'descending' } : { key, direction: 'ascending' }));
  }, []);

  // Daily sales
 const allDailySales = useMemo(() => {
  const grouped = {};

  allOrders
    .filter((o) => o.status === 'SUCCESS')
    .forEach((o) => {
      const rawDate = o.date || o.createdAt;

      if (!rawDate) return;

      const date = String(rawDate).split('T')[0];

      if (!grouped[date]) {
        grouped[date] = { date, sales: 0, orders: 0 };
      }

      grouped[date].sales += Number(o.amount || 0);
      grouped[date].orders += 1;
    });

  return Object.values(grouped).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );
}, [allOrders]);


  const dailySalesForChart = useMemo(() => {
    const start = filterStartDate ? startOfDay(filterStartDate) : undefined;
    const end = filterEndDate ? endOfDay(filterEndDate) : undefined;
    return allDailySales.filter((s) => {
      const d = parseISO(s.date);
      if (start && end) return isWithinInterval(d, { start, end });
      if (start) return d >= start;
      if (end) return d <= end;
      return d >= startOfDay(subDays(new Date(), 6));
    });
  }, [allDailySales, filterStartDate, filterEndDate]);

  // Export Excel
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      sortedItems.map((i) => ({
        Date: i.date ? new Date(i.date).toLocaleString() : "N/A",
        Customer: i.customerName,
        Email: i.email,
        Phone: i.phone,
        Product: i.productName,
        Price: i.amount,
        Status: i.status,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, 'Orders');
    XLSX.writeFile(wb, 'sales_report.xlsx', { compression: true });
  };

  return (
    <div className="container mx-auto py-8 px-4 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-primary">Dashboard</h1>
        <p className="text-muted-foreground mt-1 text-lg">Welcome back! Here's your sales performance.</p>
      </div>

      {/* Date Filter */}
      <DateRangeFilter
        startDate={filterStartDate}
        endDate={filterEndDate}
        onDateRangeChange={({ startDate, endDate }) => {
          setFilterStartDate(startDate);
          setFilterEndDate(endDate);
        }}
      />

      {/* Stat Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <StatCard title="Today's Sales" value={`₹${(statsData?.totalSalesToday ?? 0).toFixed(2)}`} icon={IndianRupeeIcon} iconColor="text-green-500" />
        <StatCard title="Today's Orders" value={statsData?.totalOrdersToday ?? 0} icon={ShoppingBag} iconColor="text-blue-500" />
        <StatCard title="Customers" value={statsData?.transactionCount ?? 0} icon={Users} iconColor="text-violet-500" />
      </div>

      {/* Chart */}
      <DailySalesChart data={dailySalesForChart} dateRange={{ startDate: filterStartDate, endDate: filterEndDate }} />

      {/* Sales Report */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row gap-4 items-center w-full">
          <div>
            <CardTitle>Sales Report</CardTitle>
            <CardDescription>Manage sales data and export to Excel.</CardDescription>
          </div>
          <Button onClick={handleExportExcel} className="flex gap-2">
            <Download className="h-4 w-4" /> Download Excel
          </Button>
        </CardHeader>
        <CardContent>
          <SalesReportFilters searchTerm={searchTerm} onSearchTermChange={setSearchTerm} onResetSearch={() => setSearchTerm('')} />
          <Separator className="my-4" />
          {isLoading ? (
            <p className="p-4">Loading orders...</p>
          ) : error ? (
            <p className="p-4 text-red-500">Failed to fetch orders.</p>
          ) : (
            <Table>
              <TableCaption>Recent orders</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead onClick={() => handleRequestSort('date')}>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead onClick={() => handleRequestSort('amount')}>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedItems.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{i.date ? new Date(i.date).toLocaleDateString() : "N/A"}</TableCell>
                    <TableCell>{i.customerName}</TableCell>
                    <TableCell>{i.email}</TableCell>
                    <TableCell>{i.phone}</TableCell>
                    <TableCell>{i.productName}</TableCell>
                    <TableCell>₹{i.amount}</TableCell>

                    <TableCell className={
                      i.status === 'SUCCESS' ? 'text-green-600 font-medium' :
                      i.status === 'FAILED' ? 'text-red-600 font-medium' :
                      'text-yellow-600 font-medium'
                    }>
                      {i.status}
                    </TableCell>
                    <TableCell className="text-right flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => navigate(`/admin/orders/${i.id}/edit`)}
                        title="Edit"
                        aria-label={`Edit order ${i.id}`}
                      >
                        <Edit size={18} />
                      </Button>
                     <Button
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        if (confirm(`Are you sure you want to delete order ${i.id}?`)) {
                          try {
                            const res = await deleteOrder(i.id).unwrap();
                            alert(res.message || "Order deleted successfully ✅");
                          } catch (err) {
                            alert("Failed to delete order ❌");
                            console.error(err);
                          }
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
